import { getRegistrationLoginType } from 'components/Pages/Registration/Registration';
import { useSocialLoginDataContext } from 'context/SocialLoginDataProvider/SocialLoginDataProvider';
import { LoginTypeEnumApi, RegistrationDataInputApi, useRegistrationMutationApi } from 'graphql/generated';
import { removeSocialLoginDataCookie } from 'helpers/cookies/removeSocialLoginData';
import { blurInput } from 'helpers/forms/blurInput';
import { formUnmasking } from 'helpers/forms/formUnmasking';
import { useRouter } from 'next/router';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { setTokensToCookie } from 'utils/Auth/TokensFromCookies';
import { onRegistrationGtmEventHandler } from 'utils/Gtm/EventHandlers';

export const useRegistration = () => {
    const [, registerMutation] = useRegistrationMutationApi();
    const router = useRouter();
    const cartUuid = usePersistStore((store) => store.cartUuid);
    const wishlistUuid = usePersistStore((store) => store.wishlistUuid);
    const updateUserEntryState = usePersistStore((s) => s.updateUserEntryState);
    const updateCartUuid = usePersistStore((store) => store.updateCartUuid);
    const { updateUserState, updateAuthLoadingState } = usePersistStore((s) => s);
    const { updatePageLoadingState } = useSessionStore((s) => s);
    const socialLoginData = useSocialLoginDataContext();

    const register = async (registrationInput: Omit<RegistrationDataInputApi, 'productListsUuids'>) => {
        blurInput();

        const registerResult = await registerMutation({
            ...registrationInput,
            companyCustomer: registrationInput.companyCustomer ?? false,
            companyNumber: registrationInput.companyNumber ?? '',
            previousCartUuid: cartUuid,
            postcode: formUnmasking(registrationInput.postcode, 'postcode'),
            loginType: getRegistrationLoginType(socialLoginData) as LoginTypeEnumApi,
            productListsUuids: wishlistUuid === null ? [] : [wishlistUuid],
        });

        if (registerResult.data?.Register) {
            const accessToken = registerResult.data.Register.tokens.accessToken;
            const refreshToken = registerResult.data.Register.tokens.refreshToken;

            const uuid = registerResult.data.Register.uuid;
            const email = registerResult.data.Register.email;

            updateCartUuid(uuid);
            updateUserState({ userEmail: email });
            setTokensToCookie(accessToken, refreshToken);
            removeSocialLoginDataCookie();
            updateUserEntryState('registration');
            updateAuthLoadingState(
                registerResult.data.Register.showCartMergeInfo
                    ? 'registration-loading-with-cart-modifications'
                    : 'registration-loading',
            );
            updatePageLoadingState({ isPageLoading: true, redirectPageType: 'homepage' });
            onRegistrationGtmEventHandler('formular');
            router.replace('/').then(() => router.reload());
        }

        return registerResult.error;
    };

    return register;
};
